import React, { createContext, useReducer, useMemo } from "react";
import { IWhatsAppState, initialWhatsAppState } from "./WhatsApp.types";
import { whatsAppReducer } from "./WhatsApp.reducer";
import { createWhatsAppActions } from "./WhatsApp.actions";

// ─── Context Value Type ──────────────────────────────────────────

export interface IWhatsAppContextValue {
  state: IWhatsAppState;
  actions: ReturnType<typeof createWhatsAppActions>;
}

// ─── Create Context ──────────────────────────────────────────────

export const WhatsAppContext = createContext<IWhatsAppContextValue | undefined>(undefined);

// ─── Provider Component ──────────────────────────────────────────
// Note: Socket handling lives in WhatsAppList.tsx which updates contacts
// directly via actions.setContacts(). This provider just manages the state.

export const WhatsAppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(whatsAppReducer, initialWhatsAppState);

  // Memoize actions so they don't recreate on every render
  const actions = useMemo(() => createWhatsAppActions(dispatch), [dispatch]);

  // ─── Memoize context value ───────────────────────────────────
  const value = useMemo(() => ({ state, actions }), [state, actions]);

  return (
    <WhatsAppContext.Provider value={value}>
      {children}
    </WhatsAppContext.Provider>
  );
};
